import { defineStore } from 'pinia';
import CarBookingAPI from 'dashboard/api/carBooking';
import { isoAt } from 'dashboard/components/carBooking/dateTime';

const blank = () => ({
  step: 'trip',
  ratePlans: [],
  // Providers the agent chose to search, and the rate plan picked for each.
  // Keyed by provider so changing one provider's plan cannot move another's.
  providers: [],
  planByProvider: {},
  form: {},
  offers: [],
  searchErrors: [],
  // Bumped on every successful search. Compare snapshots record the id they were
  // taken under, which is what makes a stale snapshot detectable after a re-search.
  searchId: 0,
  compare: [],
  shared: [],
  selected: null,
  insurances: [],
  equipment: [],
  quote: null,
  booking: null,
  detailToken: null,
  detailReturn: 'offers',
  detailQuote: null,
  schedules: {},
});

// Identifies a vehicle across searches. Offer tokens are positional and offerIds are
// ephemeral, so neither survives a re-search; provider + plan + acriss + model does.
export const offerKey = offer =>
  [offer.provider, offer.ratePlan, offer.acriss, offer.sampleModel].join('::');

export const useCarBookingStore = defineStore('carBooking', {
  state: () => ({
    byConversation: {},
    uiFlags: {
      searching: false,
      sharing: false,
      booking: false,
      prefilling: false,
      quoting: false,
      modifying: false,
      sharingQuote: false,
    },
  }),

  getters: {
    sessionFor: state => id => state.byConversation[id] || blank(),

    // Providers are derived from the rate-plan catalog rather than a separate
    // endpoint — /v1/cars/rate-plans is the only place the gateway names them.
    availableProviders: state => id => {
      const plans = (state.byConversation[id] || blank()).ratePlans || [];
      return [...new Set(plans.map(plan => plan.provider).filter(Boolean))];
    },

    plansForProvider: state => (id, provider) =>
      ((state.byConversation[id] || blank()).ratePlans || []).filter(
        plan => plan.provider === provider
      ),
  },

  actions: {
    ensure(id) {
      if (!this.byConversation[id]) this.byConversation[id] = blank();
      return this.byConversation[id];
    },

    setForm(id, form) {
      this.ensure(id).form = { ...this.ensure(id).form, ...form };
    },

    setStep(id, step) {
      this.ensure(id).step = step;
    },

    setSelected(id, token) {
      this.ensure(id).selected = token;
    },

    setProviders(id, slugs) {
      const session = this.ensure(id);
      session.providers = [...slugs];
      // Give every newly selected provider its default plan so a search is always
      // fully specified; the agent can override any of them afterwards.
      slugs.forEach(provider => {
        if (session.planByProvider[provider]) return;
        const plans = this.plansForProvider(id, provider);
        const preferred = plans.find(plan => plan.isDefault) || plans[0];
        if (preferred) session.planByProvider[provider] = preferred.slug;
      });
    },

    toggleProvider(id, provider) {
      const session = this.ensure(id);
      const next = session.providers.includes(provider)
        ? session.providers.filter(slug => slug !== provider)
        : [...session.providers, provider];
      this.setProviders(id, next);
    },

    setPlanForProvider(id, provider, slug) {
      this.ensure(id).planByProvider[provider] = slug;
    },

    // One leg per (provider, rate plan). The gateway takes a single `ratePlan` per
    // request body, so this is what keeps the providers' rate plans independent.
    legsFor(id) {
      const session = this.ensure(id);
      return session.providers.map(provider => ({
        provider,
        ratePlan: session.planByProvider[provider],
      }));
    },

    setInsurances(id, codes) {
      this.ensure(id).insurances = codes;
    },

    setEquipment(id, items) {
      this.ensure(id).equipment = items;
    },

    reset(id) {
      this.byConversation[id] = blank();
    },

    isCompared(id, offer) {
      const key = offerKey(offer);
      return this.ensure(id).compare.some(entry => entry.key === key);
    },

    // Stores a full snapshot rather than a token reference: tokens are reassigned by
    // the next search, so a reference-only tray would silently point at another car.
    // The snapshot keeps the comparison readable afterwards, and `searchId` marks
    // whether its price is still the one the current search returned.
    toggleCompare(id, offer) {
      const session = this.ensure(id);
      const key = offerKey(offer);
      const existing = session.compare.findIndex(entry => entry.key === key);
      if (existing !== -1) {
        session.compare.splice(existing, 1);
        return;
      }
      session.compare.push({
        key,
        searchId: session.searchId,
        token: offer.token,
        provider: offer.provider,
        ratePlan: offer.ratePlan,
        offer: { ...offer },
      });
    },

    removeCompare(id, key) {
      const session = this.ensure(id);
      session.compare = session.compare.filter(entry => entry.key !== key);
    },

    clearCompare(id) {
      this.ensure(id).compare = [];
    },

    // Single source of truth for the search body, so the trip screen, the offers
    // screen's "update results" and a rate-plan change mid-flow all send the same
    // shape and cannot drift apart.
    searchFromForm(id) {
      const { form } = this.ensure(id);
      return this.search(id, {
        pickup: {
          locationCode: form.pickupLocationCode,
          at: isoAt(form.pickupDate, form.pickupTime),
        },
        dropoff: {
          locationCode: form.dropoffLocationCode,
          at: isoAt(form.dropoffDate, form.dropoffTime),
        },
        driver: {
          age: Number(form.driverAge),
          countryOfResidence: form.countryOfResidence,
        },
      });
    },

    // Re-prices the selected offer with the chosen insurances/equipment and stores
    // the breakdown. Returns { outcome } on 409 when the offer is no longer
    // available, or { error } on any other failure (e.g. a TravelRouter::ApiError)
    // so the caller can render a message instead of blanking the pricing area.
    async quote(id, { token, insurances = [], equipment = [] }) {
      this.uiFlags.quoting = true;
      try {
        const { data } = await CarBookingAPI.quote(id, {
          token,
          insurances,
          equipment,
        });
        this.ensure(id).quote = data;
        return data;
      } catch (err) {
        const outcome = err.response?.data?.outcome;
        if (outcome) return { outcome };
        return { error: err.response?.data?.detail || null };
      } finally {
        this.uiFlags.quoting = false;
      }
    },

    openDetail(id, token) {
      const s = this.ensure(id);
      s.detailReturn = s.step;
      s.detailToken = token;
      s.detailQuote = null;
      s.step = 'detail';
    },

    closeDetail(id) {
      const s = this.ensure(id);
      s.step = s.detailReturn || 'offers';
    },

    // Prices an offer for the read-only detail view. Stored on session.detailQuote
    // so it never clobbers BookStep's editable quote. Returns { outcome } on 409,
    // or { error } on any other failure (e.g. a TravelRouter::ApiError) so the
    // caller can render a message instead of blanking the pricing area.
    async quotePreview(id, { token, insurances = [], equipment = [] }) {
      this.uiFlags.quoting = true;
      try {
        const { data } = await CarBookingAPI.quote(id, {
          token,
          insurances,
          equipment,
        });
        this.ensure(id).detailQuote = data;
        return data;
      } catch (err) {
        const outcome = err.response?.data?.outcome;
        if (outcome) return { outcome };
        return { error: err.response?.data?.detail || null };
      } finally {
        this.uiFlags.quoting = false;
      }
    },

    // Posts the itemized quote message to the customer as a side effect.
    // Returns { outcome } on 409 when the offer is no longer available.
    async shareQuote(id, { token, insurances = [], equipment = [] }) {
      this.uiFlags.sharingQuote = true;
      try {
        const { data } = await CarBookingAPI.shareQuote(id, {
          token,
          insurances,
          equipment,
        });
        return data;
      } catch (err) {
        const outcome = err.response?.data?.outcome;
        if (outcome) return { outcome };
        throw err;
      } finally {
        this.uiFlags.sharingQuote = false;
      }
    },

    // Fetches a station's weekly schedule (cached by code on the session).
    // Returns [] on any failure so the UI imposes no time constraints rather
    // than blocking the happy path on a schedule lookup error.
    async fetchSchedule(id, code) {
      if (!code) return [];
      const session = this.ensure(id);
      if (session.schedules[code]) return session.schedules[code];
      try {
        const { data } = await CarBookingAPI.schedule(id, code);
        const entries = Array.isArray(data)
          ? data
          : data?.entries || data?.schedule || [];
        session.schedules = { ...session.schedules, [code]: entries };
        return entries;
      } catch (e) {
        return [];
      }
    },

    async fetchEquipment(id, params) {
      const { data } = await CarBookingAPI.equipment(id, params);
      return Array.isArray(data) ? data : data?.equipment || [];
    },

    async fetchRatePlans(id) {
      const { data } = await CarBookingAPI.ratePlans(id);
      const plans = Array.isArray(data) ? data : data?.ratePlans || [];
      this.ensure(id).ratePlans = plans;
      return plans;
    },

    async modify(id, bookingId, changes) {
      this.uiFlags.modifying = true;
      try {
        const { data } = await CarBookingAPI.modify(id, bookingId, changes);
        this.ensure(id).booking = data.booking;
        return data; // { booking, rebooked }
      } finally {
        this.uiFlags.modifying = false;
      }
    },

    async prefill(id) {
      this.uiFlags.prefilling = true;
      try {
        const { data } = await CarBookingAPI.prefill(id);
        this.setForm(id, data);
        return data;
      } finally {
        this.uiFlags.prefilling = false;
      }
    },

    async search(id, search) {
      this.uiFlags.searching = true;
      try {
        // Legs are attached here so every search call site sends the agent's
        // provider/rate-plan choices without duplicating the mapping.
        const legs = this.legsFor(id);
        const payload = legs.length ? { ...search, legs } : search;
        const { data } = await CarBookingAPI.search(id, payload);
        const session = this.ensure(id);
        session.offers = data.offers;
        session.searchErrors = data.errors || [];
        session.searchId += 1;
        // Offer tokens (c1..cN) are positional — a new search reassigns them to
        // different vehicles. Anything keyed by a token from the previous search
        // is now meaningless and must be dropped so a stale token never gets
        // shared/booked as if it still pointed at the car the customer saw.
        session.shared = [];
        session.selected = null;
        session.quote = null;
        session.detailQuote = null;
        // The compare tray is the deliberate exception: it holds snapshots, not
        // token references, so it survives. Re-point the entries whose vehicle is
        // still on offer and leave the rest marked stale by their older searchId.
        session.compare = session.compare.map(entry => {
          const match = session.offers.find(o => offerKey(o) === entry.key);
          if (!match) return entry;
          return {
            ...entry,
            token: match.token,
            searchId: session.searchId,
            offer: { ...match },
          };
        });
        this.setStep(id, 'offers');
      } finally {
        this.uiFlags.searching = false;
      }
    },

    async share(id, tokens) {
      this.uiFlags.sharing = true;
      try {
        await CarBookingAPI.share(id, tokens);
        this.ensure(id).shared = tokens;
        this.setStep(id, 'choice');
      } finally {
        this.uiFlags.sharing = false;
      }
    },

    async book(id, payload) {
      this.uiFlags.booking = true;
      try {
        const { data } = await CarBookingAPI.book(id, payload);
        this.ensure(id).booking = data;
        this.setStep(id, 'booked');
        return data;
      } catch (err) {
        // Backend returns HTTP 409 for non-OK booking outcomes (price_changed,
        // unavailable). Axios throws on 4xx — intercept and return the outcome
        // object as a normal result so BookStep can branch without try/catch.
        const outcome = err.response?.data?.outcome;
        if (outcome) return err.response.data;
        throw err;
      } finally {
        this.uiFlags.booking = false;
      }
    },

    async cancel(id, bookingId) {
      const { data } = await CarBookingAPI.cancel(id, bookingId);
      this.ensure(id).booking = data;
      return data;
    },
  },
});
